import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button, Card, ChipGroup, ErrorNote, Field, Spinner, Textarea } from "../components/ui";
import { titleCase } from "../lib/format";

// The one screen that matters most: a prompt, and an honest set of options.
//
// The surfaces and features come from /api/catalog — the same maps the
// blueprint validator enforces — so this page can never offer something the
// generator would reject.

const EXAMPLES = [
  "An invoicing app for a small design studio: clients, invoices with a status and a due date, and a running total of what is outstanding.",
  "A job tracker for a plumbing business — customers, scheduled jobs with a priority, and today's work visible on a phone.",
  "A booking system for a yoga studio: classes, instructors, and bookings with a confirmation status.",
  "An expense tracker for a small team, with categories, monthly budgets and a receipt image per expense.",
];

export default function NewProject() {
  const navigate = useNavigate();
  const { data: catalog } = useQuery({ queryKey: ["catalog"], queryFn: api.catalog });

  const [prompt, setPrompt] = useState("");
  const [surfaces, setSurfaces] = useState<string[]>(["backend", "web"]);
  const [features, setFeatures] = useState<string[]>(["auth"]);

  const create = useMutation({
    mutationFn: () =>
      api.projects.create({ prompt: prompt.trim(), surfaces, features, plan: true }),
    onSuccess: (res) => navigate(`/app/projects/${res.project.id}`),
  });

  function toggle(list: string[], set: (v: string[]) => void, key: string) {
    set(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  }

  if (!catalog) return <Spinner label="Loading what Atelier can build…" />;

  const tooShort = prompt.trim().length < 10;

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">What should we build?</h1>
        <p className="mt-1 text-sm text-muted">
          Describe it the way you would to a colleague. Atelier designs the data model, generates every
          surface, compiles and tests it, then deploys it.
        </p>
      </header>

      {create.error ? (
        <div className="mb-4">
          <ErrorNote error={create.error} />
        </div>
      ) : null}

      <Card className="p-5">
        <div className="flex flex-col gap-5">
          <Field
            label="The app"
            hint="The more concretely you describe the things it tracks, the better the data model."
          >
            <Textarea
              value={prompt}
              onChange={setPrompt}
              rows={5}
              autoFocus
              placeholder="An invoicing app for a design studio…"
            />
          </Field>

          <div>
            <p className="mb-2 text-2xs uppercase tracking-wide text-muted">Or start from one of these</p>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLES.map((e, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPrompt(e)}
                  className="rounded-full border border-hairline px-3 py-1 text-2xs text-muted transition hover:border-clay/60 hover:text-ink"
                >
                  {e.split(":")[0].split("—")[0].trim()}
                </button>
              ))}
            </div>
          </div>

          <Field label="Surfaces" hint="The backend is always generated — the clients talk to it.">
            <ChipGroup
              options={catalog.surfaces.map((s) => ({
                key: s.key,
                label: s.key,
                description: s.description,
              }))}
              selected={surfaces}
              onToggle={(k) => toggle(surfaces, setSurfaces, k)}
              // The backend cannot be switched off: a client with no server to
              // talk to is not a deliverable app, and the validator would add
              // it back anyway.
              disabled={(k) => k === "backend"}
            />
          </Field>

          <Field label="Features" hint="Each is a reviewed, production-tested module — not a hint to a model.">
            <ChipGroup
              options={catalog.features.map((f) => ({
                key: f.key,
                label: titleCase(f.key),
                description: f.description,
              }))}
              selected={features}
              onToggle={(k) => toggle(features, setFeatures, k)}
            />
          </Field>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
            <p className="text-xs text-muted">
              Planning is free. A build costs {catalog.pricing.build_credits} credits, a deploy{" "}
              {catalog.pricing.deploy_credits}. Failed builds are refunded.
            </p>
            <Button onClick={() => create.mutate()} disabled={tooShort || create.isPending}>
              {create.isPending ? "Designing…" : "Design the app"}
            </Button>
          </div>
        </div>
      </Card>

      <p className="mt-5 text-center text-2xs leading-relaxed text-muted">
        Every generated app comes with structured logging, Prometheus metrics, a health endpoint, CI, and a
        deploy that gates on <code className="font-mono">/health</code> and rolls itself back.
      </p>
    </div>
  );
}
